import { Container } from '@mui/material';
import React, { useCallback, useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory, useParams } from 'react-router-dom';

import OneLinkSummaryComponent from '../../components/SummaryComponentMUI/OneLinkSummaryComponent';
import { CUSTOM } from '../../constants/campaigns';
import { routes } from '../../constants/routing';
import { useHeaderDispatch } from '../../contexts/HeaderInfo';
import { OneLinkSummaryProvider, TOLInventoryInfo, TUpdateOneLinkChunk } from '../../contexts/OneLinkSummaryProvider';
import {
  activateOneLinkRequest,
  clearOneLinkCampaigns,
  disableOneLinkRequest,
  editOneLinkRequest,
  fetchOneLinkByIdRequest,
  populateOneLinkCampaignSummariesRequest,
} from '../../store/actions/oneLink';
import { fetchBusinessReasonsRequest } from '../../store/actions/reasons';
import { fetchUsersRequest } from '../../store/actions/users';
import { selectOneLinkById } from '../../store/selectors/oneLink';
import { IInventoryItem } from '../../types/inventories';
import { IOneLinkWithCampaignSummaries, OneLinkStatusEnum } from '../../types/oneLink';
import { IOneLinkRouteParams } from '../../types/routing';
import { IApiError, NotificationListEnum } from '../../types/shell';
import notification from '../../utils/notification';
import { handleApiError } from '../../utils/ui';

const OneLinkSummaryContainer = () => {
  const history = useHistory();
  const dispatch = useDispatch();
  const { itemId, status } = useParams<IOneLinkRouteParams>();
  const lastApiStatus = useRef<string | null>(null);
  const isInitialLoad = useRef(true);
  const isHandlingStatusChange = useRef(false);

  const { setAction, setTitle } = useHeaderDispatch();

  const oneLink = useSelector(selectOneLinkById(itemId, status));

  const fetchOneLink = useCallback(
    (isSilent?: boolean) => {
      return new Promise<void>((resolve, reject) => {
        dispatch(
          fetchOneLinkByIdRequest(
            {
              itemId,
              status,
              resolve,
              reject,
            },
            { isSilent },
          ),
        );
      }).catch(
        handleApiError(`Something bad happened during the One Link retrieval.`, () =>
          history.push(routes.oneLinkManager.root),
        ),
      );
    },
    [dispatch, history, status, itemId],
  );

  const isSingleCampaignSend = useCallback(
    (info: TOLInventoryInfo): info is { items: IInventoryItem[]; pre_created_engagement_id: string } => {
      return info && 'pre_created_engagement_id' in info && 'items' in info;
    },
    [],
  );

  const isPYGSend = (info: TOLInventoryInfo): info is { pick_campaign_ids: string[] } => {
    return 'pick_campaign_ids' in info && Array.isArray(info.pick_campaign_ids) && info.pick_campaign_ids.length > 0;
  };

  const fetchCampaignSummaries = useCallback(
    (campaignIds: string[], isSilent?: boolean) => {
      dispatch(populateOneLinkCampaignSummariesRequest({ campaign_ids: campaignIds, itemId, status }, { isSilent }));
    },
    [itemId, status],
  );

  const clearCampaignSummaries = useCallback(
    (isSilent?: boolean) => {
      dispatch(clearOneLinkCampaigns({ itemId, status }, { isSilent }));
    },
    [itemId, status],
  );

  useEffect(() => {
    const handleStatusSync = async () => {
      if (!oneLink) {
        await fetchOneLink();
        return;
      }

      // Skip the effect on initial load to prevent unnecessary URL updates
      if (isInitialLoad.current) {
        isInitialLoad.current = false;
        lastApiStatus.current = oneLink.status;
        return;
      }

      // Update our reference of what the API returned
      if (oneLink.status !== lastApiStatus.current) {
        lastApiStatus.current = oneLink.status;

        // Only update URL if the current URL status doesn't match the API status
        // and we're not already in the middle of handling a status change
        if (status !== oneLink.status && !isHandlingStatusChange.current) {
          isHandlingStatusChange.current = true;
          history.replace(
            routes.oneLinkManager.getOneLinkManagerUrl({
              itemId: oneLink.uid,
              status: oneLink.status,
            }),
          );
          // Reset the flag after navigation
          setTimeout(() => {
            isHandlingStatusChange.current = false;
          }, 100);
        }
      }
    };

    handleStatusSync();
  }, [oneLink, status, history, itemId, fetchOneLink]);

  // Handle URL status changes (when user navigates manually or from external links)
  useEffect(() => {
    // Only refetch if we have a status mismatch from URL navigation
    if (lastApiStatus.current && status !== lastApiStatus.current && !isHandlingStatusChange.current) {
      fetchOneLink();
    }
  }, [status, fetchOneLink]);

  useEffect(() => {
    dispatch(fetchUsersRequest());
    dispatch(fetchBusinessReasonsRequest());
  }, [dispatch]);

  // Effect that requests campaign summaries
  // It should be triggered in case we didn't populate summaries earlier
  // It should be triggered in case there's a pre_created_engagement_id
  // It should be triggered in case there are pick_campaign_ids
  // It should NOT be triggered if we change PYG to Single campaign
  useEffect(() => {
    const campaignIDs = [];

    if (oneLink && !oneLink.pick_campaigns) {
      if (!!oneLink?.pre_created_engagement_id && oneLink.pre_created_engagement_id !== CUSTOM) {
        campaignIDs.push(oneLink.pre_created_engagement_id);
      }
      if (!!oneLink?.pick_campaign_ids?.length) {
        campaignIDs.push(...oneLink.pick_campaign_ids);
      }

      if (campaignIDs.length && !oneLink.pre_created_engagement_id) {
        fetchCampaignSummaries(campaignIDs, true);
      }
    }
  }, [oneLink, fetchCampaignSummaries]);

  useEffect(() => {
    setTitle?.('One Link Summary');
    setAction?.(() => history.push(routes.oneLinkManager.root));
  }, [setTitle, setAction, history]);

  const handleUpdate = useCallback(
    (chunk: TUpdateOneLinkChunk): Promise<IApiError | void> => {
      const { uid, status: oneLinkStatus } = oneLink || {};

      return new Promise<IApiError | void>((resolve, reject) => {
        dispatch(
          editOneLinkRequest({
            oneLink: {
              uid: uid!,
              status: oneLinkStatus!,
              ...chunk,
            },
            resolve,
            reject,
          }),
        );
      })
        .then((response) => {
          // if this is an inventory chunk and the inventory is changed from single campaign
          // to PYG we should fetch campaign summaries again to update them
          // or remove the campaign summaries from redux at all if converted to a single send

          const inventoryInfo = chunk.inventory_info;

          if (inventoryInfo) {
            if (isSingleCampaignSend(inventoryInfo)) {
              clearCampaignSummaries(true);
            }

            if (isPYGSend(inventoryInfo)) {
              fetchCampaignSummaries(inventoryInfo.pick_campaign_ids, true);
            }
          }

          const { message } = (response as IApiError) || {};
          if (message && message?.length > 0) {
            notification.warning(NotificationListEnum.EditOneLink, {
              content: message,
            });
          } else {
            notification.success(NotificationListEnum.EditOneLink, {
              content: 'One Link Send was updated successfully!',
            });
          }
        })
        .then(() => {
          fetchOneLink(true);
        })
        .catch((error) => {
          notification.error(NotificationListEnum.EditOneLink, {
            content: error?.message ? error.message : 'Something bad has happened. One Link Send edit was not applied.',
          });
        });
    },
    [fetchOneLink, oneLink?.uid, oneLink?.status, dispatch],
  );

  const handleDisableOneLink = React.useCallback(
    ({ uid }: IOneLinkWithCampaignSummaries) => {
      return new Promise<string>((resolve, reject) => {
        dispatch(disableOneLinkRequest({ uid, resolve, reject }));
      })
        .then(() =>
          history.push(
            routes.oneLinkManager.getOneLinkManagerUrl({ itemId: uid, status: OneLinkStatusEnum.NotActive }),
          ),
        )
        .then(() => {
          notification.success(NotificationListEnum.DisableOneLink, {
            content: 'One Link Send was disabled successfully!',
          });
        })
        .catch((error) => {
          notification.error(NotificationListEnum.DisableOneLink, {
            content: error?.message ? error.message : 'Something bad has happened. One Link Send was not deactivated.',
          });
        });
    },
    [history, dispatch],
  );

  const handleActivateOneLink = useCallback(
    async ({ uid }: IOneLinkWithCampaignSummaries) => {
      return new Promise<string>((resolve, reject) => {
        dispatch(activateOneLinkRequest({ uid, resolve, reject }));
      })
        .then(() => {
          history.replace(
            routes.oneLinkManager.getOneLinkManagerUrl({ itemId: uid, status: OneLinkStatusEnum.Active }),
          );
        })
        .then(() => {
          notification.success(NotificationListEnum.ActivateOneLink, {
            content: 'One Link Send was activated successfully!',
          });
        })
        .catch((error: IApiError) => {
          notification.error(NotificationListEnum.ActivateOneLink, {
            content: error?.message ? error.message : 'Something bad has happened. One Link Send was not activated.',
          });

          throw error;
        });
    },
    [dispatch],
  );

  return (
    <OneLinkSummaryProvider
      onUpdate={handleUpdate}
      onDeactivate={handleDisableOneLink}
      onActivate={handleActivateOneLink}
      instance={oneLink ?? null}
    >
      <Container maxWidth="xl" sx={{ marginTop: 2 }}>
        <OneLinkSummaryComponent reportMode />
      </Container>
    </OneLinkSummaryProvider>
  );
};

export default OneLinkSummaryContainer;
